import { Alert, Avatar, Button, Typography } from "antd";
import type { ReactNode } from "react";
import { IdentityUserText } from "./identity-user-text";
import "./auth-shell.css";

export type SsoLoginWorkspaceStatus =
  | "bootstrap_pending"
  | "resolved_sso_user"
  | "no_session"
  | "oidc_failed"
  | "bootstrap_failed"
  | "no_access";

export type SsoLoginWorkspaceUser = {
  username: string;
  full_name?: string | null;
  avatar_url?: string | null;
};

export type SsoLoginWorkspaceUserField<TUser extends SsoLoginWorkspaceUser> = {
  key: string;
  label: ReactNode;
  value: ReactNode | ((user: TUser) => ReactNode);
};

type SsoLoginWorkspaceCopy = {
  bootstrapTitle: ReactNode;
  bootstrapDescription?: ReactNode;
  noSessionTitle: ReactNode;
  noSessionDescription?: ReactNode;
  oidcFailedTitle?: ReactNode;
  oidcFailedDescription?: ReactNode;
  bootstrapFailedTitle?: ReactNode;
  bootstrapFailedDescription?: ReactNode;
  noAccessTitle: ReactNode;
  noAccessDescription?: ReactNode;
  portalAction?: ReactNode;
  enterAction: ReactNode;
  switchIdentityAction: ReactNode;
  redetectAction: ReactNode;
};

export type SsoLoginWorkspaceProps<TUser extends SsoLoginWorkspaceUser> = {
  status: SsoLoginWorkspaceStatus;
  resolvedUser: TUser | null;
  copy: SsoLoginWorkspaceCopy;
  onPortal?: () => void | Promise<void>;
  onEnter?: () => void | Promise<void>;
  onSwitchIdentity: () => void | Promise<void>;
  onRedetect: () => void | Promise<void>;
  portalLoading?: boolean;
  enterLoading?: boolean;
  switchIdentityLoading?: boolean;
  redetectLoading?: boolean;
  userFields?: SsoLoginWorkspaceUserField<TUser>[];
  bootstrapError?: ReactNode;
};

function resolveFieldValue<TUser extends SsoLoginWorkspaceUser>(
  user: TUser,
  value: ReactNode | ((currentUser: TUser) => ReactNode),
) {
  return typeof value === "function" ? value(user) : value;
}

export function SsoLoginWorkspace<TUser extends SsoLoginWorkspaceUser>({
  status,
  resolvedUser,
  copy,
  onPortal,
  onEnter,
  onSwitchIdentity,
  onRedetect,
  portalLoading = false,
  enterLoading = false,
  switchIdentityLoading = false,
  redetectLoading = false,
  userFields = [],
  bootstrapError,
}: SsoLoginWorkspaceProps<TUser>) {
  if (status === "bootstrap_pending") {
    return (
      <div className="airange-login-sso-panel">
        <div className="airange-login-sso-statecard airange-login-sso-statecard--info">
          <Alert
            type="info"
            showIcon
            className="airange-login-alert"
            title={copy.bootstrapTitle}
            description={copy.bootstrapDescription}
          />
          <div className="airange-login-sso-actions">
            {copy.portalAction ? (
              <Button
                color="primary"
                variant="solid"
                size="large"
                block
                loading={portalLoading}
                onClick={() => void onPortal?.()}
              >
                {copy.portalAction}
              </Button>
            ) : null}
            <Button size="large" block loading={redetectLoading} onClick={() => void onRedetect()}>
              {copy.redetectAction}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "resolved_sso_user" && resolvedUser) {
    const displayName = resolvedUser.full_name || resolvedUser.username;

    return (
      <div className="airange-login-sso-panel">
        <div className="airange-login-sso-usercard">
          <div className="airange-login-sso-profile">
            <Avatar size={56} src={resolvedUser.avatar_url || undefined}>
              {displayName.slice(0, 1).toUpperCase()}
            </Avatar>
            <div className="airange-login-sso-profile-copy">
              <Typography.Title level={5} style={{ margin: 0 }}>
                {displayName}
              </Typography.Title>
              <IdentityUserText
                displayText={
                  <Typography.Text type="secondary">{resolvedUser.username}</Typography.Text>
                }
                username={resolvedUser.username}
                {...(resolvedUser.full_name === undefined ? {} : { fullName: resolvedUser.full_name })}
              />
            </div>
          </div>

          {userFields.map((field) => (
            <div key={field.key} className="airange-login-sso-userrow">
              <span className="airange-login-sso-label">{field.label}</span>
              <span className="airange-login-sso-value">
                {resolveFieldValue(resolvedUser, field.value)}
              </span>
            </div>
          ))}

          <Button
            color="primary"
            variant="solid"
            size="large"
            block
            loading={enterLoading}
            onClick={() => void onEnter?.()}
          >
            {copy.enterAction}
          </Button>
          <Button
            size="large"
            block
            loading={switchIdentityLoading}
            onClick={() => void onSwitchIdentity()}
          >
            {copy.switchIdentityAction}
          </Button>
        </div>
      </div>
    );
  }

  if (status === "no_access") {
    return (
      <div className="airange-login-sso-panel">
        <div className="airange-login-sso-statecard airange-login-sso-statecard--error">
          <div className="airange-login-sso-statecard__header">
            <Typography.Title level={5} className="airange-login-sso-statecard__title">
              {copy.noAccessTitle}
            </Typography.Title>
            {copy.noAccessDescription ? (
              <Typography.Paragraph className="airange-login-sso-statecard__desc">
                {copy.noAccessDescription}
              </Typography.Paragraph>
            ) : null}
          </div>
          <Button
            size="large"
            block
            loading={switchIdentityLoading}
            onClick={() => void onSwitchIdentity()}
          >
            {copy.switchIdentityAction}
          </Button>
          <Button size="large" block loading={redetectLoading} onClick={() => void onRedetect()}>
            {copy.redetectAction}
          </Button>
        </div>
      </div>
    );
  }

  if (status === "oidc_failed") {
    return (
      <div className="airange-login-sso-panel">
        <div className="airange-login-sso-statecard airange-login-sso-statecard--error">
          <div className="airange-login-sso-statecard__header">
            <Typography.Title level={5} className="airange-login-sso-statecard__title">
              {copy.oidcFailedTitle ?? copy.bootstrapFailedTitle ?? copy.bootstrapTitle}
            </Typography.Title>
            {copy.oidcFailedDescription ? (
              <Typography.Paragraph className="airange-login-sso-statecard__desc">
                {copy.oidcFailedDescription}
              </Typography.Paragraph>
            ) : null}
          </div>
          <div className="airange-login-sso-actions">
            {copy.portalAction ? (
              <Button
                color="primary"
                variant="solid"
                size="large"
                block
                loading={portalLoading}
                onClick={() => void onPortal?.()}
              >
                {copy.portalAction}
              </Button>
            ) : null}
            <Button size="large" block loading={redetectLoading} onClick={() => void onRedetect()}>
              {copy.redetectAction}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (status === "bootstrap_failed") {
    return (
      <div className="airange-login-sso-panel">
        <div className="airange-login-sso-statecard airange-login-sso-statecard--error">
          <div className="airange-login-sso-statecard__header">
            <Typography.Title level={5} className="airange-login-sso-statecard__title">
              {copy.bootstrapFailedTitle ?? copy.bootstrapTitle}
            </Typography.Title>
            {copy.bootstrapFailedDescription || bootstrapError ? (
              <Typography.Paragraph className="airange-login-sso-statecard__desc">
                {copy.bootstrapFailedDescription}
                {bootstrapError ? (
                  <>
                    {copy.bootstrapFailedDescription ? <br /> : null}
                    {bootstrapError}
                  </>
                ) : null}
              </Typography.Paragraph>
            ) : null}
          </div>
          <div className="airange-login-sso-actions">
            {copy.portalAction ? (
              <Button
                color="primary"
                variant="solid"
                size="large"
                block
                loading={portalLoading}
                onClick={() => void onPortal?.()}
              >
                {copy.portalAction}
              </Button>
            ) : null}
            <Button size="large" block loading={redetectLoading} onClick={() => void onRedetect()}>
              {copy.redetectAction}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="airange-login-sso-panel">
      <div className="airange-login-sso-statecard airange-login-sso-statecard--warning">
        <div className="airange-login-sso-statecard__header">
          <Typography.Title level={5} className="airange-login-sso-statecard__title">
            {copy.noSessionTitle}
          </Typography.Title>
          {copy.noSessionDescription ? (
            <Typography.Paragraph className="airange-login-sso-statecard__desc">
              {copy.noSessionDescription}
            </Typography.Paragraph>
          ) : null}
        </div>
        <div className="airange-login-sso-actions">
          {copy.portalAction ? (
            <Button
              color="primary"
              variant="solid"
              size="large"
              block
              loading={portalLoading}
              onClick={() => void onPortal?.()}
            >
              {copy.portalAction}
            </Button>
          ) : null}
          <Button
            size="large"
            block
            loading={switchIdentityLoading}
            onClick={() => void onSwitchIdentity()}
          >
            {copy.switchIdentityAction}
          </Button>
          <Button size="large" block loading={redetectLoading} onClick={() => void onRedetect()}>
            {copy.redetectAction}
          </Button>
        </div>
      </div>
    </div>
  );
}
